import React, { useMemo, useState } from 'react';
import { Grid, Icon, Tooltip, Typography } from '@saviynt/design-system';

import { type UserCount } from '../../../../services/types';
import { useInvertedLocalize } from '../../../../utils';
import DonutChart from '../DonutChart/DonutChart';

import invertedMessages from './messages';

import './Analytics.css';

const DEFAULT_COLOR = '#ccc' as const;
const VISIBLE_LEGEND_ITEMS = 3 as const;

interface LegendSeparatorProps {
  color: string;
}

function LegendSeparator({ color }: LegendSeparatorProps) {
  return (
    <div
      className='Ext-LegendSeparator'
      style={{
        backgroundColor: color, // Line color
      }}
    />
  );
}

interface LegendItemProps {
  type: string;
  color?: string;
  count: number;
  percentage: string;
}

function LegendItem({ type, color, count, percentage }: LegendItemProps) {
  return (
    <div key={type} className='Ext-Analytics-container-identities-legend-item'>
      <LegendSeparator color={color ?? DEFAULT_COLOR} />
      <div className='Ext-Analytics-container-identities-legend-item-content'>
        <div className='Ext-Analytics-container-identities-legend-item-content-count'>
          <Typography
            kind='body1-bold'
            color='--color-foreground-secondary-bold'>
            {count}
          </Typography>
          <Typography
            kind='body1-bold'
            color='--color-foreground-neutral-subtle'>
            ({`${percentage}%`})
          </Typography>
        </div>
        <Typography kind='body2' lineLimit={1}>
          {`${type}`}
        </Typography>
      </div>
    </div>
  );
}

interface AnalyticsProps {
  data: UserCount;
}

export default function Analytics({ data: { data, total } }: AnalyticsProps) {
  const l = useInvertedLocalize(invertedMessages);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const { visibleItems, otherItems } = useMemo(
    () => ({
      visibleItems: data.slice(0, VISIBLE_LEGEND_ITEMS),
      otherItems: data.slice(VISIBLE_LEGEND_ITEMS),
    }),
    [data]
  );
  const { Item } = Grid;

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect(); // Get position of the hovered element

    setTooltipPosition({
      top: rect.bottom,
      left: rect.left,
    });
    setTooltipVisible(true); // Show the tooltip
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false); // Hide the tooltip
  };

  return (
    <section className='Ext-Analytics'>
      <Grid className='Ext-Analytics-container' spacing={2}>
        <Item
          className='Ext-Analytics-container-identities'
          desktop={9}
          tablet={12}>
          <Grid>
            <Item
              className='Ext-Analytics-container-identities-chart'
              desktop={3}
              tablet={12}>
              <DonutChart
                widthRem={12}
                heightRem={9}
                data={data}
                total={total}
              />
            </Item>
            <Item
              className='Ext-Analytics-container-identities-chart-details'
              desktop={9}
              tablet={12}>
              <div className='Ext-Analytics-container-identities-chart-details-header'>
                <Tooltip
                  trigger={
                    <Typography
                      kind='display'
                      color='--color-foreground-secondary-bold'>
                      {total}
                    </Typography>
                  }
                  text={l('External Identities based on classification rules')}
                  placement='top'
                  openDelay={0}
                />
                <Typography
                  kind='body1'
                  color='--color-foreground-neutral-subtle'>
                  {l('External identities')}
                </Typography>
                <Tooltip
                  trigger={<Icon kind='Info' color='indigo-800' />}
                  text={l(
                    'This section presents the distribution of external identities based on employee type.'
                  )}
                  placement='top'
                  openDelay={0}
                  className='Ext-Analytics-container-identities-chart-details-header-infoTooltip'
                />
              </div>
              <div className='Ext-Analytics-container-identities-legend-wrapper'>
                <div className='Ext-Analytics-container-identities-legend'>
                  {visibleItems.map((d) => (
                    <LegendItem
                      type={d.type}
                      color={d.color}
                      count={d.count}
                      percentage={d.percentage}
                    />
                  ))}

                  {/* "Others" Section */}
                  {data.length > 3 && (
                    <>
                      <div
                        className='Ext-Analytics-container-identities-legend-item-others'
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}>
                        <div className='Ext-Analytics-container-identities-legend-item-separator others' />
                        <div className='Ext-Analytics-container-identities-legend-item-content'>
                          <Typography kind='body2'>Others</Typography>
                        </div>
                      </div>

                      {/* Hidden Legends for "Others" */}
                      {tooltipVisible && (
                        <div
                          className='Ext-Analytics-container-identities-legend-others'
                          style={{
                            top: tooltipPosition.top,
                            left: tooltipPosition.left,
                          }}
                          onMouseEnter={() => setTooltipVisible(true)} // Keep it visible while hovering over the tooltip
                          onMouseLeave={handleMouseLeave} // Hide it when leaving the tooltip
                        >
                          {otherItems.map((d) => (
                            <LegendItem
                              key={d.type}
                              type={d.type}
                              color={d.color}
                              count={d.count}
                              percentage={d.percentage}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Item>
          </Grid>
        </Item>
      </Grid>
    </section>
  );
}

LegendItem.defaultProps = {
  color: '',
};
